<template>
  <div class="register-container">
    <div class="register-content">
      <div
        v-if="!isActivationLink && !isPasswordResetLink"
        class="title-section"
      >
        <h1 v-if="!coBrandCompanyName">Sign up to create your free will</h1>
        <h1 v-else-if="bannerTitle">{{ bannerTitle }}</h1>
        <h1 v-else>
          As an {{ coBrandCompanyName }} customer, you can draft your will for
          free
        </h1>
        <p v-if="!coBrandCompanyName" class="login-header-text">
          The <strong>first step</strong> in
          <strong>setting up your will</strong> is
          <span class="green-text">creating a profile</span> that is linked to
          your email address.<br />
          <br />
          <span
            >We will be using this email address to send all our communications
            to you.</span
          ><br /><br />
          <span class="mt-2 d-block">Use email or Google to continue.</span>
        </p>
        <div v-else-if="bannerDescription">
          <p>{{ bannerDescription }}</p>
        </div>
        <div v-else>
          <p>
            We've partnered with {{ coBrandCompanyName }} to offer you a free
            will
          </p>
        </div>
      </div>
      <div v-else-if="isActivationLink" class="title-section">
        <h1>Create a password to activate your account</h1>
      </div>
      <div v-else-if="isPasswordResetLink" class="title-section">
        <h1>Enter a new password</h1>
      </div>
      <div class="error-section">
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      </div>
      <div class="top-section">
        <SignUpWith
          :key="stableKey"
          @onError="onError"
          @loginClicked="loginClicked"
          @signupMethodFocused="signupMethodFocused"
          :showContent="showContent"
          :isActivationLink="isActivationLink"
          :isPasswordResetLink="isPasswordResetLink"
          :verifyUserLinkCode="verifyUserLinkCode"
          :isReferralLink="isReferralLink"
          :interactedWith="interactedWith"
        ></SignUpWith>
      </div>
      <div class="or-hr-section">
        <OrHr
          v-if="
            !isActivationLink &&
            !isPasswordResetLink &&
            whitelabel.brand === 'oldmutual'
          "
        />
      </div>
      <div class="bottom-section">
        <GoogleSsoSignUp
          v-if="
            !isActivationLink &&
            !isPasswordResetLink &&
            whitelabel.brand === 'oldmutual'
          "
          @onError="onError"
          @ssoButtonClicked="signupWithGoogleSsoClicked"
          :accepted-terms="acceptedTerms"
          :submitClicked="ssoSubmitClicked"
        ></GoogleSsoSignUp>
        <div>
          <ValidationObserver ref="observer" v-if="interactedWith === 'google'">
            <ValidationProvider
              id="accepted_terms"
              v-slot="{ errors }"
              name="accepted_terms"
              :rules="isPasswordResetLink ? {} : { is: [true] }"
            >
              <v-checkbox
                v-if="!isPasswordResetLink"
                v-model="acceptedTerms"
                color="primary"
                :error-messages="errors"
              >
                <template #label>
                  <div class="terms-and-conditions-container">
                    By signing up, I accept
                    <a
                      v-if="['oldmutual', 'teba'].includes(whitelabel.brand)"
                      :href="
                        $router.resolve({ name: 'TermsAndConditions' }).href
                      "
                      target="_blank"
                      @click.stop
                    >
                      {{ whitelabel.name }}'s terms & conditions
                    </a>
                    <a
                      v-if="whitelabel.brand == 'quickwill'"
                      target="_blank"
                      :href="whitelabel.termsAndConditionsLocation"
                      @click.stop
                      >{{ whitelabel.name }}'s terms & conditions</a
                    >
                  </div>
                </template>
              </v-checkbox>
            </ValidationProvider>
          </ValidationObserver>
        </div>
        <div>
          <AlreadyHaveAnAccount
            v-if="!isPasswordResetLink"
            :type="'signup'"
            @loginClicked="loginClicked"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import GoogleSsoSignUp from '../GoogleSsoSignUp.vue';
  import SignUpWith from '../SignUpWith.vue';
  import { mapGetters } from 'vuex';
  import AlreadyHaveAnAccount from '../AlreadyHaveAnAccount.vue';
  import OrHr from '../../common/OrHr.vue';

  export default {
    name: 'RegisterForm',
    components: {
      GoogleSsoSignUp,
      SignUpWith,
      OrHr,
      AlreadyHaveAnAccount,
    },
    data() {
      return {
        acceptedTerms: false,
        acceptedCompetitionTerms: false,
        errorMessage: '',
        formRef: null,
        showContent: true,
        ssoSubmitClicked: null,
        interactedWith: 'email',
        stableKey: 'some-stable-key',
      };
    },
    computed: {
      ...mapGetters(['coBrandCompanyUrl']),
      ...mapGetters(['coBrandCompanyName']),
      ...mapGetters(['bannerTitle']),
      ...mapGetters(['bannerDescription']),
    },
    watch: {},
    props: {
      isReferralLink: {
        required: false,
        default: false,
        type: Boolean,
      },
      isActivationLink: {
        required: false,
        default: false,
        type: Boolean,
      },
      verifyUserLinkCode: {
        required: false,
        default: null,
        type: Object,
      },
      isPasswordResetLink: {
        required: false,
        default: false,
        type: Boolean,
      },
    },
    methods: {
      signupMethodFocused(method) {
        this.interactedWith = method;
      },
      signupWithGoogleSsoClicked() {
        this.signupMethodFocused('google');

        this.$nextTick(() => {
          this.$refs.observer.validate().then(valid => {
            if (valid) {
              this.ssoSubmitClicked = Date.now();
            } else {
              const errors = this.$refs.observer.ctx.errors;
              document
                .getElementById(
                  Object.keys(errors).find(field => errors[field].length)
                )
                .scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
          });
        });
      },
      loginClicked() {
        this.$emit('loginClicked');
      },
      onError(error) {
        if (!this.acceptedTerms) {
          if (this.$refs.observer) {
            this.$refs.observer.$refs.observer.validate();
          }
          if (this.$refs.signupobserver) {
            this.$refs.signupobserver.$refs.observer.validate();
          }
          if (this.$refs.signupobserverterms) {
            this.$refs.signupobserverterms.$refs.observer.validate();
          }
        }
        if (error) {
          this.errorMessage = error.message
            .replace('GraphQL error:', '')
            .trim();
        } else {
          this.errorMessage = '';
        }
      },
      termsAndConditionsUpdated(accepted) {
        this.acceptedTerms = accepted;
      },
      competitionTermsAndConditionsUpdated(accepted) {
        this.acceptedCompetitionTerms = accepted;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .register-container {
    display: flex;
    justify-content: center;
    padding: 0 30px;
    .register-content {
      width: clamp(360px, 22dvw, 400px);
      padding-top: 155px;
    }
    .login-header-text {
      font-size: 16px;
      line-height: 22px;
    }
    .signup-title {
      font-size: 16px;
      line-height: 22px;
    }

    .sso-btn {
      margin-bottom: 14px;
    }

    .bottom-section {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;

      .terms-and-conditions-container {
        font-size: 12px;
        font-weight: 500;
        line-height: 18px;
        text-align: left;
        color: #282828;
        a {
          font-size: 12px;
          font-weight: 500;
          line-height: 18px;
          text-align: left;
          color: #282828;
        }
      }
    }

    .title-section {
      h1 {
        font-size: 22px;
        font-weight: 500;
        line-height: 30px;
        margin-bottom: 10px;
      }
      p {
        font-size: 14px;
        font-weight: 400;
        line-height: 18.2px;
        margin: 0;
      }
    }
    .top-section {
      margin-top: 22px;
    }
    .or-hr-section {
      margin-top: 30px;
      margin-bottom: 30px;
    }

    .error-section {
      p {
        margin: 0;
        margin-top: 16px;
        margin-bottom: 16px;
      }
    }
  }
  .green-text {
    color: #50b848;
    font-weight: bolder;
  }
</style>
